import React, { useState, useCallback, useEffect, useRef } from "react";
import { gql, useQuery } from '@apollo/client';
import { Bell, Sun, Moon, ChevronDown, User, Settings, Plug, LogOut, PanelLeftClose, PanelLeftOpen, Brain } from "lucide-react";
import { SIDEBAR_USER_QUERY } from "./AppSidebar";
import { GEORGE_CONVERSATION_FEATURE, GEORGE_UNDERSTANDING_FEATURE } from "../../../constants/succeedoConstants";
import { DEFAULT_COACH_NAME } from "../Conversation/copy";

const GET_HEADER_USER = gql`
  query GetHeaderUser {
    currentUser {
      id
      name
      succeedo_persona
      profile_picture
      coach_profile {
        id
        is_partner
        avatar_url
      }
    }
  }
`;

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function AppHeader({
  breadcrumb = "DASHBOARD",
  persona: personaProp,
  userName: userNameProp,
  userInitials: userInitialsProp,
  hasNotifications = true,
  // From AppLayout: the sidebar's collapsed state and its toggle. Without a
  // toggle (a page rendering the header on its own) no button shows.
  sidebarCollapsed = false,
  onToggleSidebar,
}) {
  // Fetch user data from Apollo cache (populated by page containers)
  const { data } = useQuery(GET_HEADER_USER, { fetchPolicy: 'cache-first' });
  const currentUser = data?.currentUser;
  // George P4-2: what the coach knows about the owner and their controls. It
  // is a setting more than a place, so it lives in this menu, not the sidebar.
  const { data: coachData } = useQuery(SIDEBAR_USER_QUERY, { fetchPolicy: 'cache-first' });
  const features = coachData?.company?.features || [];
  const understandingEnabled = features.includes(GEORGE_CONVERSATION_FEATURE) && features.includes(GEORGE_UNDERSTANDING_FEATURE);
  const coachName = coachData?.coaching_conversation?.coach_name || DEFAULT_COACH_NAME;

  const userName = userNameProp || currentUser?.name || '';
  const userInitials = userInitialsProp || (userName ? userName.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) : '');
  // Partner is a distinct persona — no buyer/seller axis. Show a static
  // PARTNER badge instead of the toggle. Derived from coach_profile.is_partner
  // (canonical) so it's consistent across header + sidebar.
  const isPartner = currentUser?.coach_profile?.is_partner === true;
  const persona = isPartner
    ? 'PARTNER'
    : (personaProp || (currentUser?.succeedo_persona || 'seller').toUpperCase());
  // SMB tenants get null from resolve_profile_picture when no custom avatar is set,
  // so any truthy value is a real avatar. Partners use coach_profile.avatar_url.
  const avatarUrl = (isPartner ? currentUser?.coach_profile?.avatar_url : null) || currentUser?.profile_picture || null;

  // Avatar URLs can 401 (e.g. a CoachProfile.avatar_url stored before the
  // authorized-URL fix landed); fall back to initials when the image
  // request fails. Reset on URL change so a re-upload gets a fresh try.
  const [avatarErrored, setAvatarErrored] = useState(false);
  useEffect(() => {
    setAvatarErrored(false);
  }, [avatarUrl]);
  const showAvatar = avatarUrl && !avatarErrored;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      try { return localStorage.getItem('succeedo_dark_mode') === '1'; } catch {}
    }
    return false;
  });
  const dropdownRef = useRef(null);

  // Apply dark mode class on mount and when toggled
  useEffect(() => {
    const app = document.querySelector('.succeedo-app');
    if (!app) return;
    if (darkMode) {
      app.classList.add('dark');
    } else {
      app.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    const next = !darkMode;
    setDarkMode(next);
    try { localStorage.setItem('succeedo_dark_mode', next ? '1' : '0'); } catch {}
  }, [darkMode]);

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  // Close on Escape key
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeDropdown();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [dropdownOpen, closeDropdown]);

  return (
    <header className="fixed top-0 left-0 right-0 h-14 bg-card/80 border-b border-border backdrop-blur-[20px] z-50">
      <div className="h-full px-4 md:px-6 flex items-center justify-between gap-3">
        {/* Left: Logo (with spacer for hamburger on mobile) */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-8 md:hidden" />
          {onToggleSidebar && (
            <button
              type="button"
              onClick={onToggleSidebar}
              className="hidden md:inline-flex items-center justify-center w-8 h-8 -ml-1 rounded-[8px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!sidebarCollapsed}
              aria-controls="succeedo-sidebar"
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed
                ? <PanelLeftOpen className="w-[18px] h-[18px]" />
                : <PanelLeftClose className="w-[18px] h-[18px]" />}
            </button>
          )}
          <img
            src={darkMode
              ? "https://findbob-production.s3.us-west-2.amazonaws.com/images/succeedo_white_brand.png"
              : "https://findbob-production.s3.us-west-2.amazonaws.com/images/succeedo_brand.png"}
            alt="Succeedo"
            className="h-5 md:h-6"
          />
        </div>

        {/* Center: Breadcrumb — hidden on mobile/tablet */}
        <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 font-mono text-[11px] uppercase text-muted-foreground/35 tracking-wide">
          {breadcrumb}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3 md:gap-5 shrink-0">
          {/* Dark Mode Toggle */}
          <button
            type="button"
            onClick={toggleDarkMode}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          </button>

          {/* Notifications */}
          <button
            type="button"
            className="relative text-muted-foreground hover:text-foreground transition-colors p-1"
            aria-label="Notifications"
          >
            <Bell className="w-[18px] h-[18px]" />
            {hasNotifications && (
              <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[hsl(var(--succeedo-orange))]" />
            )}
          </button>

          {/* Persona Pill — hidden on small mobile */}
          <div className="hidden sm:block rounded-full border border-border text-muted-foreground px-3 py-1 font-mono text-[10px] uppercase">
            {persona}
          </div>

          {/* User Avatar */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-8 h-8 rounded-full bg-muted text-foreground text-[12px] font-semibold flex items-center justify-center hover:ring-1 hover:ring-border transition-all shrink-0 overflow-hidden"
              aria-label="User menu"
              aria-expanded={dropdownOpen}
            >
              {showAvatar ? (
                <img
                  src={avatarUrl}
                  alt={userName}
                  onError={() => setAvatarErrored(true)}
                  className="w-full h-full object-cover"
                />
              ) : (
                userInitials
              )}
            </button>

            {/* Dropdown */}
            {dropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={closeDropdown}
                />
                <div className="absolute top-full right-0 mt-2 w-[220px] bg-card border border-border rounded-[12px] shadow-2xl z-50">
                  {/* User Info */}
                  <div className="p-4 flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-muted text-foreground text-[14px] font-semibold flex items-center justify-center overflow-hidden">
                      {showAvatar ? (
                        <img
                          src={avatarUrl}
                          alt={userName}
                          onError={() => setAvatarErrored(true)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        userInitials
                      )}
                    </div>
                    <div className="text-[14px] font-semibold text-foreground">
                      {userName}
                    </div>
                    <div className="font-mono text-[10px] uppercase text-muted-foreground/40">
                      {persona}
                    </div>
                  </div>

                  <div className="h-px bg-border" />

                  {/* Menu Items — partner-aware. Partners go to the partner
                      profile editor; owners use the legacy seller profile route. */}
                  <div className="py-1">
                    <a
                      href={isPartner ? '/succeedo/partner/profile/edit' : '/succeedo/profile'}
                      className="w-full px-4 py-2 flex items-center gap-3 text-[14px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <User className="w-4 h-4" />
                      {isPartner ? 'Edit Profile' : 'My Profile'}
                    </a>
                    {!isPartner && understandingEnabled && (
                      <a
                        href="/succeedo/george/knows"
                        className="w-full px-4 py-2 flex items-center gap-3 text-[14px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      >
                        <Brain className="w-4 h-4" />
                        What {coachName} knows
                      </a>
                    )}
                    {!isPartner && (
                      <a
                        href="/succeedo/settings"
                        className="w-full px-4 py-2 flex items-center gap-3 text-[14px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </a>
                    )}
                    <a
                      href="/succeedo/integrations"
                      className="w-full px-4 py-2 flex items-center gap-3 text-[14px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <Plug className="w-4 h-4" />
                      Integrations
                    </a>
                  </div>

                  <div className="h-px bg-border" />

                  {/* Sign Out */}
                  <div className="py-1">
                    <a
                      href="/logout"
                      className="w-full px-4 py-2 flex items-center gap-3 text-[14px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
